<template>
  <panel class="mt-6 pt-6">
    <loader v-if="leads === null" />
    <div>
      <h3 class="font-medium text-lg mb-4">Lead Library</h3>
      <div class="flex flex-wrap items-center -mx-2 mb-6">
        <div class="w-full md:w-1/2 px-2">
          <input type="text" v-model="searchParams.q" placeholder="Search" class="input-sm w-full rounded" />
        </div>
      </div>
      <div v-if="leads.data.length === 0">
        Your search returned no results
      </div>
      <div v-else class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-left w-full">
          <thead class="uppercase text-xs">
            <tr>
              <th class="p-2 whitespace-no-wrap">ID</th>
              <th class="p-2 whitespace-no-wrap">Type</th>
              <th class="p-2 whitespace-no-wrap">Department</th>
              <th class="p-2 whitespace-no-wrap">Vendor</th>
              <th class="p-2 whitespace-no-wrap">Property</th>
              <th class="p-2 whitespace-no-wrap">Date Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="text-sm">
            <tr v-for="lead in leads.data" :key="lead.id" class="border-t border-grey-lighter hover:bg-grey-lightest cursor-pointer" @click="activeModal = lead.id">
              <td class="p-2">
                {{ lead.id }}
              </td>
              <td class="p-2">
                {{ lead.valuation_type }}
              </td>
              <td class="p-2">
                {{ lead.rental ? 'Lettings' : 'Sales' }}
              </td>
              <td class="p-2">
                {{ lead.name }}
              </td>
              <td class="p-2">
                {{ lead.address }}
                <small class="block text-grey-dark mt-1" v-if="lead.property_type">
                  <span v-if="lead.bedrooms">{{ lead.bedrooms }} bed </span>{{ lead.property_type }}
                </small>
              </td>
              <td class="p-2">
                {{ lead.created | datetime }}
              </td>
              <td class="text-right">
                <button type="button" class="bg-tertiary text-white p-2 my-2 rounded">
                  More Information
                </button>
                <modal
                  v-if="activeModal === lead.id"
                  @leave="activeModal = null"
                  :showCloseBtn="true"
                >
                  <div class="text-left">
                    <h3 class="font-medium text-lg mb-4 font-bold">{{ lead.address }}</h3>
                    <p>{{ lead.valuation_type }} {{ lead.rental ? 'Lettings' : 'Sales' }} Valuation Request</p>
                    <p class="text-xs text-grey-dark mt-1">Lead ID: {{ lead.id }}. Received at: {{ lead.created | datetime }}</p>

                    <table class="w-full mt-6">
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Vendor</td>
                        <td class="p-2">{{ lead.name }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Phone Number</td>
                        <td class="p-2">{{ lead.phone }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">E-Mail Address</td>
                        <td class="p-2"><a :href="`mailto:${lead.email}`" class="text-primary">{{ lead.email }}</a></td>
                      </tr>
                      <tr class="border-t border-grey-lighter">
                        <td class="p-2 font-bold whitespace-no-wrap">Property Address</td>
                        <td class="p-2">{{ lead.address }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.property_type">
                        <td class="p-2 font-bold whitespace-no-wrap">Property Type</td>
                        <td class="p-2">{{ lead.property_type }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.bedrooms">
                        <td class="p-2 font-bold whitespace-no-wrap">Bedrooms</td>
                        <td class="p-2">{{ lead.bedrooms }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.bathrooms">
                        <td class="p-2 font-bold whitespace-no-wrap">Bathrooms</td>
                        <td class="p-2">{{ lead.bathrooms }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.reception_rooms">
                        <td class="p-2 font-bold whitespace-no-wrap">Reception Rooms</td>
                        <td class="p-2">{{ lead.reception_rooms }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.year_built">
                        <td class="p-2 font-bold whitespace-no-wrap">Year Built</td>
                        <td class="p-2">{{ lead.year_built }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.land_size">
                        <td class="p-2 font-bold whitespace-no-wrap">Land Size</td>
                        <td class="p-2">{{ lead.land_size }} acre(s)</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.condition">
                        <td class="p-2 font-bold whitespace-no-wrap">Condition</td>
                        <td class="p-2">{{ lead.condition }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.features">
                        <td class="p-2 font-bold whitespace-no-wrap">Features</td>
                        <td class="p-2">
                          <ul>
                            <li v-for="(feature, i) in lead.features" :key="i">
                              {{ feature }}
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.alterations">
                        <td class="p-2 font-bold whitespace-no-wrap">Alterations</td>
                        <td class="p-2">{{ lead.alterations }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.notes">
                        <td class="p-2 font-bold whitespace-no-wrap">Notes</td>
                        <td class="p-2">{{ lead.notes }}</td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.preferred_times.length">
                        <td class="p-2 font-bold whitespace-no-wrap">Preferred Times</td>
                        <td class="p-2">
                          <div v-for="(preferred_time, i) in lead.preferred_times" :key="i" :class="{'mb-3': i<lead.preferred_times.length - 1}">
                            <strong class="font-bold block">{{ preferred_time.date | verboseDate(false) }}</strong>
                            <div class="mt-1 text-grey-dark">
                              {{ preferred_time.times.join(', ') }}
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr class="border-t border-grey-lighter" v-if="lead.attachments.length">
                        <td class="p-2 font-bold whitespace-no-wrap align-top">Attachments</td>
                        <td class="p-2">
                          <div class="p-2 bg-grey-lighter rounded mb-2">
                            <h3 class="font-semibold mb-2">Attention</h3>
                            <p>All attachments are uploaded onto this service by the sellers and have not been pre-processed for any viruses.
                            All users accessing these attachments do so at their own risk and should take care when downloading any attachment,
                            ensuring virus protection systems are installed and are up to date.</p>
                          </div>
                          <ul class="list-reset">
                            <li v-for="(attachment, i) in lead.attachments" :key="i" class="mb-2">
                              <a :href="attachment.url" download class="text-primary" target="_blank">{{ attachment.filename }}</a>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </table>
                  </div>
                </modal>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <paginator :data="leads.meta" @change="pageChanged" />
    </div>

    <div class="mt-6">
      <h3 class="font-medium text-lg mb-4">Download CSV</h3>

      <div class="flex flex-wrap items-end mb-4 -mx-4">
        <div class="w-full md:w-1/3 lg:w-1/4 mb-4 md:mb-0 px-4">
          <label class="label-xs">From</label>
          <date-time-picker v-model="report.from" format="d MMMM yyyy" value-format="yyyy-MM-dd" :time-picker="false" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm" :max-date="report.to"  />
        </div>

        <div class="w-full md:w-1/3 lg:w-1/4 mb-4 md:mb-0 px-4">
          <label class="label-xs">To</label>
          <date-time-picker v-model="report.to" format="d MMMM yyyy" value-format="yyyy-MM-dd" :time-picker="false" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm" :min-date="report.from"  />
        </div>

        <button
          class="bg-secondary text-white rounded mx-4 px-6 py-2 w-1/2 md:w-1/3 lg:w-1/6"
          @click="downloadReport('office')"
        >
          <span v-if="userOfficeList.length > 1 && [1, 2, 3, 4].includes(profile_v2.type)">Download This Office</span><span v-else>Download</span>
        </button>

        <button
          class="bg-secondary text-white rounded mx-4 px-6 py-2 w-1/2 md:w-1/3 lg:w-1/6"
          @click="downloadReport('client')"
          v-if="userOfficeList.length > 1 && [1, 2, 3, 4].includes(profile_v2.type)"
        >
          Download All Office
        </button>
      </div>
    </div>
  </panel>
</template>

<script>
import api from '@/http'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import DateTimePicker from 'vue-vanilla-datetime-picker'

export default {
  components: {
    DateTimePicker
  },
  data () {
    return {
      leads: null,
      searchParams: {
        page: 1,
        q: ''
      },
      complete: 'complete',
      pageHasChanged: false,
      activeModal: null,
      report: {
        from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD')
      }
    }
  },

  computed: {
    ...mapState(['selectedOffice', 'userOfficeList', 'profile_v2'])
  },

  methods: {
    moment: moment,

    ...mapActions(['displayToastNotification']),

    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    },

    fetchLeads: debounce(function () {
      if (!this.selectedOffice.id) {
        return
      }

      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      this.leads = null
      api.get(`office/${this.selectedOffice.id}/engage/leads/${this.complete}`, {
        params: this.searchParams
      }).then((response) => {
        this.leads = response.data
      }).catch(() => {
        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-bell',
          title: 'Error',
          info: "We're unable to fetch your leads at this time."
        })
      })
    }, 1000),
    downloadReport (type) {
      this.report.type = type
      api
        .get(`office/${this.selectedOffice.id}/engage/report`, {
          params: this.report
        })
        .then(({ data }) => {
          let blob = new Blob([data], { type: 'text/csv' })

          if (!window.navigator.msSaveOrOpenBlob) {
            let url = window.URL.createObjectURL(blob)
            let link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'engage-report.csv')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          } else {
            window.navigator.msSaveOrOpenBlob(blob, 'engage-report.csv')
          }
        })
        .catch(() => {
          this.displayToastNotification({
            type: 'warning',
            icon: 'fa-bell',
            title: 'Download Failed',
            info: "We're unable to download the requested report at this time."
          })
        })
    }
  },

  created () {
    this.fetchLeads()
  },

  watch: {
    selectedOffice () {
      this.searchParams.page = 1
      this.fetchLeads()
    },
    searchParams: {
      handler () {
        this.fetchLeads()
      },
      deep: true
    },
    complete () {
      this.searchParams.page = 1
      this.fetchLeads()
    }
  }
}
</script>
